import React, { useCallback, useEffect, useMemo } from 'react'

import * as Sentry from '@sentry/react'
import { t, Trans } from 'src/translations'

import {
  LoyaltySignUpPage,
  PageConfig,
  useLoyaltySettingsLazyQuery,
  useLoyaltySignUpLazyQuery
} from 'src/apollo/sites'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  SubmitState,
  useSubmissionState
} from 'shared/js/hooks/useSubmissionState'
import { AdditionalPagesType } from 'shared/js/types'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { EditPathRoot } from 'public/components/default_template/main_page/MainPage'
import BetterTogetherPage from 'public/components/pages/better_together/BetterTogetherPage'
import useTracker from 'src/lib/js/hooks/useTracker'

export type LoyaltyPageType = AdditionalPagesType[0]['content'] & {
  __typename: 'LoyaltySignUpPage'
}

type LoyaltyContextType = {
  onSubmit: (data: any) => void
  programDescription: string
  useEmail: boolean
  submitState: SubmitState
  programName: string | null
}

export const LOYALTY_SIGNUP_CONTENT = (t: any) => {
  return {
    defaultHeader: t('loyalty-page.sign-up-for-rewards'),
    errorLine1: t('loyalty-page.we-had-trouble-submitting'),
    errorLine2: t('loyalty-page.please-try-again-later'),
    successLine1: t('loyalty-page.you-are-signed-up-for-rewards'),
    successLine2: t('loyalty-page.youll-earn-toward-rewards-with-every-order'),
    emailDisclaimer: <>{t('loyalty-page.email-disclaimer')}</>,
    phoneDisclaimer: (
      <Trans
        i18nKey='loyalty-page.phone-disclaimer'
        components={{
          TermsLink: (
            <a
              href='https://pos.toasttab.com/terms-of-service'
              rel='noopener noreferrer'
              target='_blank'
            />
          ),
          PrivacyLink: (
            <a
              href='https://pos.toasttab.com/privacy'
              rel='noopener noreferrer'
              target='_blank'
            />
          )
        }}
      />
    )
  }
}

export const getSuccessHeader = (programName: string | null) => {
  return programName
    ? t('loyalty-page.welcome-to-program', { programName })
    : LOYALTY_SIGNUP_CONTENT(t).successLine1
}

export const useLoyaltyContext = (): LoyaltyContextType => {
  const [geLoyaltySettings, { data: loyaltySettingsData }] =
    useLoyaltySettingsLazyQuery()
  const [loyaltySignUp, { data: loyaltySignUpResponse }] =
    useLoyaltySignUpLazyQuery()

  const { restaurant } = useRestaurant()
  const { submitState, startSubmission, endSubmission, failSubmission } =
    useSubmissionState()
  const tracker = useTracker()

  useEffect(() => {
    if (restaurant.locations && restaurant.locations[0]?.id) {
      geLoyaltySettings({
        variables: { restaurantGuid: restaurant.locations[0].externalId }
      })
    }
  }, [restaurant.locations, geLoyaltySettings])

  const loyaltySettings: any = useMemo(() => {
    return loyaltySettingsData?.loyaltySettings
  }, [loyaltySettingsData])

  useEffect(() => {
    if (loyaltySignUpResponse) {
      if (loyaltySignUpResponse.loyaltySignUp == null) {
        failSubmission()
      } else {
        tracker.track(TrackingEvents.LOYALTY_SIGNUP, {
          context: 'loyalty_page',
          restaurant_guid: restaurant.locations?.[0]?.externalId ?? null
        })
      }
      endSubmission()
    }
  }, [
    loyaltySignUpResponse,
    tracker,
    restaurant.locations,
    endSubmission,
    failSubmission
  ])

  const useEmail: boolean = useMemo(
    () => loyaltySettings?.signupMethod === 'EMAIL',
    [loyaltySettings]
  )
  const programDescription: string = useMemo(
    () => loyaltySettings?.programDescription,
    [loyaltySettings]
  )
  const programName: string = useMemo(
    () => loyaltySettings?.programName || null,
    [loyaltySettings]
  )

  const onSubmit = useCallback(
    async (data: any) => {
      startSubmission()
      try {
        if (restaurant.locations && restaurant.locations[0]?.externalId) {
          loyaltySignUp({
            variables: {
              restaurantGuid: restaurant.locations[0].externalId,
              phoneNumber: data.phone,
              email: data.email
            }
          })
        }
      } catch (error) {
        failSubmission()
        Sentry.captureMessage(`ERROR: ${error}`)
      }
    },
    [restaurant.locations, loyaltySignUp, startSubmission, failSubmission]
  )

  return {
    onSubmit,
    programDescription,
    useEmail,
    submitState,
    programName
  }
}

type Props = {
  content?: LoyaltySignUpPage
  editPathRoot?: EditPathRoot
  config?: PageConfig | null
}

const LoyaltyPage = (props: Props) => {
  const { onSubmit, programDescription, useEmail, submitState, programName } =
    useLoyaltyContext()

  return (
    <BetterTogetherPage
      content={props.content}
      editPathRoot={props.editPathRoot}
      config={props.config}
      onSubmit={onSubmit}
      useEmail={useEmail}
      header={
        props.content?.headerText || LOYALTY_SIGNUP_CONTENT(t).defaultHeader
      }
      subheader={programDescription}
      disclaimer={
        <>
          {useEmail
            ? LOYALTY_SIGNUP_CONTENT(t).emailDisclaimer
            : LOYALTY_SIGNUP_CONTENT(t).phoneDisclaimer}
        </>
      }
      {...LOYALTY_SIGNUP_CONTENT(t)}
      submitState={submitState}
      successLine1={getSuccessHeader(programName)}
    />
  )
}

export default LoyaltyPage
